import React, { useContext, useMemo, useRef } from 'react'
import { triangle1XY, triangle2XY } from './diagram-builder/triangles'
import { AppStateContext } from '../AppStateContext'
import { DataContext } from './DataContext'
import rawData from './utility/dummyData'
import Lines from './diagram-builder/Lines'
import Points from './diagram-builder/Points'
import Spinner from '../../../elem/Spinner'
import Message from '../../../elem/chart/Message'
import { getColorByFacilityType } from '../../../../utils/chart/palette'

const PiperChart = ({ isExpanded }) => {
    const {
        mapState: { selectedFeatures },
    } = useContext(AppStateContext)
    const { piperData, loading, filterMode, containerDimensions } = useContext(
        DataContext
    )

    const svgRef = useRef()

    const cationWeight = useMemo(() => {
        return piperData.map(x => {
            return {
                color: getColorByFacilityType(x.FacilityType),
                ...x,
                a: x.Calcium / x.TotalCationMeqL,
                b: (x.Sodium + x.Barium) / x.TotalCationMeqL,
                c: x.Magnesium / x.TotalCationMeqL,
            }
        })
    }, [piperData])

    const cationXY = useMemo(
        () =>
            cationWeight.map(el => {
                const { a: Ca, b: Na_Ba, c: Mg } = el
                const x = triangle1XY[0] + Na_Ba + Mg / 2
                const y = triangle1XY[1] + (Math.sqrt(3) / 2) * Mg
                return {
                    ...el,
                    x,
                    y,
                    percentages: {
                        Ca: Ca * 100,
                        Mg: Mg * 100,
                        'Na + Ba': Na_Ba * 100,
                    },
                }
            }),
        [cationWeight]
    )

    const anionWeight = useMemo(
        () =>
            piperData.map(x => {
                return {
                    ...x,
                    color: getColorByFacilityType(x.FacilityType),
                    a: x.Chloride / x.TotalAnionMeqL,
                    b: (x.NitrateN + x.Fluoride) / x.TotalAnionMeqL,
                    c: x.Sulfate / x.TotalAnionMeqL,
                }
            }),
        [piperData]
    )

    const anionXY = useMemo(
        () =>
            anionWeight.map(el => {
                const { a: Cl, b: NO3_F, c: SO4 } = el
                const x = triangle2XY[0] + Cl + SO4 / 2
                const y = triangle2XY[1] + (Math.sqrt(3) / 2) * SO4
                return {
                    ...el,
                    x,
                    y,
                    percentages: {
                        Cl: Cl * 100,
                        SO4: SO4 * 100,
                        'NO3 + F': NO3_F * 100,
                    },
                }
            }),
        [anionWeight]
    )

    const diamondXY = useMemo(() => {
        return cationXY.map((el, i) => {
            const { x: x0, y: y0 } = el
            const { x: x1, y: y1, a: ya, b: yb, c: yc } = anionXY[i]

            const sqrt3 = Math.sqrt(3)
            const x = (y1 - y0 + sqrt3 * (x1 + x0)) / (2 * sqrt3)
            const y = sqrt3 * (x - x0) + y0
            return {
                ...el,
                x,
                y,
                percentages: {
                    'SO4 + Cl': (ya + yc) * 100,
                    'Ca + Mg': (el.a + el.c) * 100,
                },
            }
        })
    }, [cationXY, anionXY])

    return loading ? (
        <Spinner extraClass="chartWrapper" />
    ) : !selectedFeatures.length && filterMode === 'Selected Wells' ? (
        <Message
            text={'No Wells Selected'}
            subText={`Select wells to generate a Piper diagram`}
        />
    ) : selectedFeatures.length && !(piperData && piperData.length) ? (
        <Message
            text={'No Data Available'}
            subText={`Select wells wtih measurable samples to generate a Piper diagram`}
        />
    ) : (
        <svg
            // preserveAspectRatio={`none`}
            // viewBox={`0 0 ${containerDimensions.width} ${containerDimensions.height}`}
            width={containerDimensions.width}
            height={containerDimensions.height}
            ref={svgRef}
            style={{
                overflow: 'visible', // Allow points near edges to be visible
                position: 'relative',
                marginLeft: isExpanded ? '-60px' : '-10px',
            }}
        >
            <Lines svgRef={svgRef} />
            <Points dataPoints={[...cationXY, ...anionXY, ...diamondXY]} />
        </svg>
    )
}

export default PiperChart
